import React, { useEffect, useState } from "react";
import api, { getRoles, inr, today, formatApiError, hasWrite } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { ArrowRightLeft, Check, X, Clock, AlertTriangle, BellRing } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";

export const useBranches = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/branches").then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  return data;
};

// Admin/accountant-only branch filter: "" = this branch, "all" = all branches, or a branch id.
export const BranchFilter = ({ value, onChange, testid = "branch-filter" }) => {
  const { user } = useAuth();
  const data = useBranches();
  const staff = user?.platform_role === "superadmin" || getRoles(user).some((r) => r === "admin" || r === "accountant");
  const mine = new Set((user?.companies || []).map((c) => c.id));
  const opts = (data?.branches || []).filter((b) => mine.has(b.id) || user?.platform_role === "superadmin");
  if (!staff || opts.length < 2) return null;
  return (
    <div><div className="text-xs text-muted-foreground mb-1">Branch</div>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
        <option value="">This branch</option>
        <option value="all">All branches</option>
        {opts.filter((b) => b.id !== user?.active_company_id).map((b) => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
      </select>
    </div>
  );
};

const RejectDialog = ({ t, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!reason.trim()) return toast.error("Please give a reason");
    setBusy(true);
    try {
      await api.post(`/branches/transfers/${t.transfer_id}/reject`, { date: today(), reason });
      toast.success("Transfer rejected — amount returned to the sending branch"); onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="bt-reject-dialog" className="max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl">Reject {inr(t.amount)} from {t.from_name}</DialogTitle></DialogHeader>
        <input data-testid="bt-reject-reason" className={field} placeholder="e.g. cash not received / amount short" value={reason} onChange={(e) => setReason(e.target.value)} />
        <DialogFooter>
          <button data-testid="bt-reject-submit" disabled={busy} onClick={go} className="h-10 px-5 rounded-md bg-destructive text-destructive-foreground text-sm disabled:opacity-50">{busy ? "Rejecting…" : "Reject"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const IncomingRow = ({ t, canWrite, busy, onConfirm, onReject }) => (
  <div data-testid={`bt-incoming-${t.transfer_id}`} className="flex flex-wrap items-center gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3">
    <ArrowRightLeft size={16} className="text-amber-600" />
    <div className="flex-1 min-w-[200px] text-sm">
      <span className="font-semibold num">{inr(t.amount)}</span> {t.mode} sent by <b>{t.from_name}</b> on {t.date}
      {t.sent_by_name ? ` (${t.sent_by_name})` : ""}{t.note ? ` — ${t.note}` : ""}
      <div className="text-xs text-muted-foreground">Confirm only when it has reached you. It will be added to today's cash book.</div>
    </div>
    {canWrite && <div className="flex gap-2">
      <button data-testid={`bt-confirm-${t.transfer_id}`} disabled={busy} onClick={() => onConfirm(t)} className="h-9 px-3 rounded-md bg-primary text-primary-foreground text-sm flex items-center gap-1.5 disabled:opacity-50"><Check size={15} /> {busy ? "Saving…" : "Received"}</button>
      <button data-testid={`bt-reject-${t.transfer_id}`} onClick={() => onReject(t)} className="h-9 px-3 rounded-md border border-input bg-card text-sm flex items-center gap-1.5 hover:bg-muted"><X size={15} /> Reject</button>
    </div>}
  </div>
);

export const PendingTransfers = ({ canWrite, onDone, refreshKey }) => {
  const [d, setD] = useState({ incoming: [], outgoing: [] });
  const [rej, setRej] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = () => api.get("/branches/transfers/pending").then((r) => setD(r.data)).catch((e) => console.error(e));
  useEffect(() => { load(); }, [refreshKey]);
  const done = () => { load(); onDone(); };
  const confirm = async (t) => {
    if (busyId) return;
    setBusyId(t.transfer_id);
    try {
      await api.post(`/branches/transfers/${t.transfer_id}/confirm`, { date: today() });
      toast.success(`${inr(t.amount)} received — added to today's cash book`); done();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
    finally { setBusyId(null); }
  };
  if (!d.incoming.length && !d.outgoing.length) return null;
  return (
    <div data-testid="bt-pending" className="space-y-2 mb-5">
      {d.incoming.map((t) => <IncomingRow key={t.transfer_id} t={t} canWrite={canWrite} busy={busyId === t.transfer_id} onConfirm={confirm} onReject={setRej} />)}
      {d.outgoing.map((t) => (
        <div key={t.transfer_id} data-testid={`bt-outgoing-${t.transfer_id}`} className="flex items-center gap-2 text-sm text-muted-foreground rounded-md border border-border bg-card px-4 py-2">
          <Clock size={14} /> <span className="num font-medium text-foreground">{inr(t.amount)}</span> sent to {t.to_name} on {t.date} — waiting for them to confirm
        </div>
      ))}
      {rej && <RejectDialog t={rej} onClose={() => setRej(null)} onDone={done} />}
    </div>
  );
};

export const OverdueTransfers = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/branches/transfers/overdue").then((r) => setItems(r.data.items || [])).catch((e) => console.error(e));
  useEffect(() => { load(); }, []);
  const remind = async (t) => {
    try { await api.post(`/branches/transfers/${t.transfer_id}/remind`); toast.success(`Reminder sent to ${t.to_name}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!items.length) return null;
  const total = items.reduce((s, t) => s + t.amount, 0);
  return (
    <div data-testid="bt-overdue-alert" className="mb-6 rounded-lg border border-destructive/40 bg-destructive/10 px-5 py-4 animate-fade-up">
      <div className="flex items-center gap-2 font-semibold text-destructive mb-2">
        <AlertTriangle size={18} /> Undelivered cash: {items.length} transfer{items.length > 1 ? "s" : ""} ({inr(total)}) not confirmed for over a day
      </div>
      <div className="space-y-1">
        {items.map((t) => (
          <div key={t.transfer_id} data-testid={`bt-overdue-${t.transfer_id}`} className="text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="num font-medium">{inr(t.amount)}</span>
            <span>{t.mode} from <b>{t.from_name}</b> to <b>{t.to_name}</b></span>
            <span className="text-muted-foreground">sent {t.date}{t.sent_by_name ? ` by ${t.sent_by_name}` : ""} · waiting {t.days} day{t.days > 1 ? "s" : ""}{t.note ? ` · ${t.note}` : ""}</span>
            <button data-testid={`bt-remind-${t.transfer_id}`} onClick={() => remind(t)} className="h-8 px-3 rounded-md border border-destructive/40 bg-card text-xs font-medium flex items-center gap-1.5 hover:bg-muted transition-colors"><BellRing size={13} /> Remind branch</button>
            {t.reminded_at && <span data-testid={`bt-reminded-${t.transfer_id}`} className="text-xs text-muted-foreground">Reminded {t.remind_count > 1 ? `${t.remind_count}×, last ` : ""}{new Date(t.reminded_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

const seenKey = (t) => `bt-remind-${t.transfer_id}-${t.reminded_at}`;

export const TransferReminderPopup = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const cid = user?.active_company_id;
  useEffect(() => {
    if (!cid || !hasWrite(user)) { return; }
    api.get("/branches/transfers/pending").then((r) => setItems((r.data.incoming || []).filter((t) => t.reminded_at && !sessionStorage.getItem(seenKey(t))))).catch((e) => console.error(e));
  }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const close = () => { items.forEach((t) => sessionStorage.setItem(seenKey(t), "1")); setItems([]); };
  if (!items.length) return null;
  return (
    <Dialog open onOpenChange={close}>
      <DialogContent data-testid="bt-reminder-popup" className="max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><BellRing size={22} className="text-destructive" /> Please confirm cash received</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {items.map((t) => (
            <div key={t.transfer_id} data-testid={`bt-reminder-${t.transfer_id}`} className="rounded-md border border-border bg-card px-3 py-2 text-sm">
              <span className="num font-semibold">{inr(t.amount)}</span> {t.mode} from <b>{t.from_name}</b>, sent {t.date}
              <div className="text-xs text-muted-foreground">{t.reminded_by_name || "Admin"} is asking you to confirm whether it arrived.</div>
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <button data-testid="bt-reminder-later" onClick={close} className="h-10 px-4 rounded-md border border-input bg-card text-sm hover:bg-muted">Later</button>
          <button data-testid="bt-reminder-open" onClick={() => { close(); nav("/cashbook"); }} className="h-10 px-5 rounded-md bg-primary text-primary-foreground text-sm">Open Cash Book</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const BranchTransferDialog = ({ date, onDone }) => {
  const { user } = useAuth();
  const data = useBranches();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ to_branch_id: "", amount: "", mode: "cash", note: "" });
  const [busy, setBusy] = useState(false);
  const others = (data?.branches || []).filter((b) => b.id !== user?.active_company_id);
  if (others.length === 0) return null;
  const submit = async () => {
    if (!f.to_branch_id) return toast.error("Pick the branch receiving the cash");
    if (!(parseFloat(f.amount) > 0)) return toast.error("Enter an amount");
    setBusy(true);
    try {
      await api.post("/branches/transfer", { ...f, amount: parseFloat(f.amount), date: date || today() });
      toast.success(`${inr(parseFloat(f.amount))} sent — waiting for the other branch to confirm`);
      setOpen(false); setF({ to_branch_id: "", amount: "", mode: "cash", note: "" }); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid="branch-transfer-btn" onClick={() => setOpen(true)} className="h-10 px-4 rounded-md bg-card border border-input text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors">
        <ArrowRightLeft size={16} /> Branch transfer
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="branch-transfer-dialog" className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Send money to another branch</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="block text-sm font-medium mb-1.5">To branch</label>
              <select data-testid="bt-to" className={field} value={f.to_branch_id} onChange={(e) => setF({ ...f, to_branch_id: e.target.value })}>
                <option value="">Select branch…</option>
                {others.map((b) => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
              </select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-medium mb-1.5">Amount (₹)</label>
                <input data-testid="bt-amount" type="number" className={`${field} num`} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
              <div><label className="block text-sm font-medium mb-1.5">From</label>
                <select data-testid="bt-mode" className={field} value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })}>
                  <option value="cash">Cash</option><option value="bank">Bank</option>
                </select></div>
            </div>
            <div><label className="block text-sm font-medium mb-1.5">Note</label>
              <input data-testid="bt-note" className={field} placeholder="e.g. sent with Ramesh" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></div>
            <p className="text-xs text-muted-foreground">Leaves this branch's {f.mode} on {date || today()}. It shows in the other branch's cash book only after they confirm it arrived. If they reject it, the amount comes back here.</p>
          </div>
          <DialogFooter>
            <button data-testid="bt-submit" disabled={busy} onClick={submit} className="h-10 px-5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50">{busy ? "Sending…" : "Send"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
